<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Appointment slot selection -->
<template>
  <div class="slot-selecton select-time">
    <div class="time-header">
      <i class="fal fa-chevron-circle-left" :class="{ disabled: !canGoBack }" @click="previousWeek()"></i>
      <span>
        {{ formatTime(displayStartDay, 'MMM do') }} - {{ formatTime(displayEndDay, 'do yyyy') }}
      </span>
      <i class="fal fa-chevron-circle-right" :class="{ disabled: !canGoForward }" @click="nextWeek()"></i>
    </div>
    <div class="available-slots" v-if="!loadingSlots">
      <div class="day" v-for="day of weekDays" :key="day">
        <div class="day-heading">{{ formatTime(day, 'E')}}<br><span>{{ formatTime(day, ' MMM do yyyy') }}</span></div>
        <div
          class="slot"
          :class="{ 'unavailable': !slot.anyReviewerAvailable, disabled: isSelectedSlot(slot.start) }"
          v-for="slot of slotsForDay(day)"
          :key="slot" @click="selectSlot(slot)"
        >
          <span class="slot-time">{{ formatTime(slot.start, 'h:mma') }}</span>
        </div>
      </div>
    </div>
    <div v-if="rescheduleForOtherReviewer" class="other-reviewer-warning">
      Because you are not available for any other time, this appointment will be rescheduled with a different reviewer.
    </div>
    <av-spinner v-if="loadingSlots" class="loading-slots">Loading Available Times</av-spinner>
    <div class="no-slots-warning" v-if="slotsForWeek.length === 0 && availabilitySlots.length > 0 && !loadingSlots">
      There are no available times this week.
    </div>
    <div class="no-slots-warning" v-if="availabilitySlots.length === 0 && !loadingSlots">
      There are no available times before your deadline.
    </div>
  </div>
</template>

<script lang="ts">
import User from '../models/user'
import { addDays, addMinutes, addWeeks, endOfWeek, format, isAfter, isBefore, startOfDay, startOfWeek, isSameHour } from 'date-fns'
import { defineComponent, PropType } from 'vue'
import { mapGetters } from 'vuex'
import AppointmentTypeService from '../common/appointmentTypeService'
import AvailabilitySlot from '../models/availabilitySlot'
export default defineComponent({
  name: 'AppointmentSlotSelection',

  props: {
    appointmentTypeId: {
      type: Number,
      required: true
    },
    currentReviewerId: {
      type: Number,
      default: 0,
      required: false
    },
    forReschedule: {
      type: Boolean,
      default: false
    },
    internalScheduling: {
      type: Boolean,
      default: false
    },
    maxDate: {
      type: Object as PropType<Date>,
      required: true
    },
    startTime: {
      type: Object as PropType<Date>,
      default: Date(),
      required: false
    }
  },

  data () {
    return {
      loadingSlots: false,
      /** list of available slots */
      availabilitySlots: [] as Array<AvailabilitySlot>,
      /** date range */
      dateRange: {
        startTime: startOfWeek(new Date()),
        endTime: endOfWeek(new Date())
      },
      rescheduleForOtherReviewer: false
    }
  },

  emits: ['slot-selected'],

  computed: {
    /** Returns true if the date range is after the current date */
    canGoBack (): boolean {
      return isAfter(this.dateRange.startTime, new Date())
    },

    /** Returns true if the date range is before the deadline */
    canGoForward (): boolean {
      return isBefore(startOfDay(addMinutes(this.dateRange.endTime, 1)), this.maxDate)
    },

    /** returns start day */
    displayStartDay (): Date {
      return addDays(this.dateRange.startTime, 1)
    },

    /** returns end day */
    displayEndDay (): Date {
      return addDays(this.dateRange.endTime, -1)
    },

    /** The slots that fall within the current time range */
    slotsForWeek (): Array<AvailabilitySlot> {
      return this.availabilitySlots.filter(slot => {
        return isAfter(slot.start, this.dateRange.startTime) && isBefore(slot.start, this.dateRange.endTime)
      })
    },

    /** Dates for the current week */
    weekDays (): Array<Date> {
      return [1, 2, 3, 4, 5].map(x => {
        return addDays(this.dateRange.startTime, x)
      })
    },

    ...mapGetters(['isAdmin', 'userId', 'firstName', 'lastName'])
  },

  mounted () {
    this.getAvailability()
  },

  watch: {
    appointmentTypeId: {
      handler: function () {
        this.dateRange.startTime = startOfWeek(new Date())
        this.dateRange.endTime = endOfWeek(new Date())
        this.getAvailability()
      },
      immediate: true
    }
  },

  methods: {
    /**
     * Formats the given date in the specified format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    /**
     * Fetches availability for the selected appointment type
     * and date range
     */
    getAvailability () {
      this.loadingSlots = true
      this.rescheduleForOtherReviewer = false
      const start = new Date()
      const end = this.maxDate

      const reviewerIsRescheduling = this.forReschedule && this.internalScheduling && !this.isAdmin

      let reviewerId = 0
      if (reviewerIsRescheduling) {
        reviewerId = this.currentReviewerId
      }

      const includeUnavailable = this.internalScheduling && this.isAdmin

      // AppointmentTypeService.getAvailability(this.appointmentTypeId, start, end, this.internalScheduling || this.forReschedule).then(res => {
      AppointmentTypeService.getAvailability(this.appointmentTypeId, start, end, reviewerId, includeUnavailable).then(res => {
        res.data.forEach(d => {
          d.start = new Date(d.start)
          d.end = new Date(d.end)
        })

        if (res.data.length > 0) {
          this.dateRange.startTime = startOfWeek(res.data[0].start)
          this.dateRange.endTime = endOfWeek(this.dateRange.startTime)
        }

        // Rescheduling by reviewer and no availability for themselves
        if (reviewerIsRescheduling && res.data.length === 0) {
          // Fetch all availability
          AppointmentTypeService.getAvailability(this.appointmentTypeId, start, end, 0, false).then(res => {
            res.data.forEach(d => {
              d.start = new Date(d.start)
              d.end = new Date(d.end)
            })

            if (res.data.length > 0) {
              this.dateRange.startTime = startOfWeek(res.data[0].start)
              this.dateRange.endTime = endOfWeek(this.dateRange.startTime)
            }

            this.rescheduleForOtherReviewer = true
            this.availabilitySlots = res.data
            this.loadingSlots = false
          })
        } else {
          this.availabilitySlots = res.data
          this.loadingSlots = false
        }
      })
    },

    /**
     * Moves the date range forward one week
     */
    nextWeek () {
      this.dateRange.startTime = addWeeks(this.dateRange.startTime, 1)
      this.dateRange.endTime = addWeeks(this.dateRange.endTime, 1)
    },

    /**
     * Moves the date range back one week
     */
    previousWeek () {
      this.dateRange.startTime = addWeeks(this.dateRange.startTime, -1)
      this.dateRange.endTime = addWeeks(this.dateRange.endTime, -1)
    },

    isSelectedSlot (currentTime: Date): boolean {
      return isSameHour(currentTime, this.startTime)
    },

    /**
     * Emits an event when a slot is selected including the selected slot
     * @param slot: AvailabilitySlot
     */
    selectSlot (slot: AvailabilitySlot) {
      if (this.forReschedule && !this.rescheduleForOtherReviewer && !this.isAdmin && this.internalScheduling) {
        slot.reviewer = { id: this.userId, firstName: this.firstName, lastName: this.lastName } as User
      }
      this.$emit('slot-selected', slot)
    },

    /**
     * Returns the slots available on the given day
     * @param day: The day in the form of a Date object
     */
    slotsForDay (day: Date): Array<AvailabilitySlot> {
      return this.slotsForWeek.filter(slot => {
        return slot.start.getDay() === day.getDay()
      }).sort((a: AvailabilitySlot, b: AvailabilitySlot) => {
        if (isBefore(a.start, b.start)) {
          return -1
        } else if (isBefore(b.start, a.start)) {
          return 1
        } else {
          return 0
        }
      })
    }
  }
})
</script>

<style scoped>
  .reschedule-modal {
    opacity: 0.2;
    pointer-events: none;
  }
</style>
